import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  claimPersonaApi,
  fetchConnectedPersonas,
  fetchPersonas,
  type ConnectedPersonaResponse,
  type PersonaResponse
} from '../api/client';
import { setSessionPersona } from '../api/session';
import { Combobox } from './Combobox';

const norm = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/**
 * Client entry point to a case: first pick the person by name (type-ahead over
 * the personas defined for the case), then the role if that person has more than
 * one. Personas already connected (by another session) show as taken. On success
 * the persona (person+role) is claimed and stored on the session.
 */
export function PersonaPicker({
  caseId,
  onClaimed
}: {
  caseId: string;
  onClaimed: (personaId: string) => void;
}) {
  const { t } = useTranslation();
  const [personas, setPersonas] = useState<PersonaResponse[]>([]);
  const [connected, setConnected] = useState<ConnectedPersonaResponse[]>([]);
  const [name, setName] = useState('');
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const [ps, conn] = await Promise.all([fetchPersonas(caseId), fetchConnectedPersonas(caseId)]);
    setPersonas(ps);
    setConnected(conn);
  }
  useEffect(() => {
    void load();
  }, [caseId]);

  const names = useMemo(() => [...new Set(personas.map((p) => p.name))], [personas]);
  const takenIds = useMemo(() => new Set(connected.map((c) => c.personaId)), [connected]);
  const rolesForName = useMemo(
    () => personas.filter((p) => p.name === selectedName),
    [personas, selectedName]
  );

  function suggestNames(q: string) {
    const query = norm(q.trim());
    return names.filter((n) => !query || norm(n).includes(query)).slice(0, 8);
  }

  function onNameChange(v: string) {
    setName(v);
    setSelectedName(names.includes(v) ? v : null);
    setError(null);
  }

  async function claim(persona: PersonaResponse) {
    setBusy(true);
    setError(null);
    const { ok, conflict } = await claimPersonaApi(caseId, persona.id);
    setBusy(false);
    if (ok) {
      setSessionPersona(caseId, {
        personaId: persona.id,
        personaName: persona.name,
        role: persona.role
      });
      onClaimed(persona.id);
      return;
    }
    if (conflict) setError(t('persona.taken'));
    await load();
  }

  return (
    <div className="persona-picker">
      <h2 className="card__title">{t('persona.pickTitle')}</h2>
      <p className="card__hint">{t('persona.pickHint')}</p>

      <label className="field">
        <span className="field__label">{t('persona.name')}</span>
        <Combobox
          value={name}
          onChange={onNameChange}
          suggest={suggestNames}
          placeholder={t('common.search')}
        />
      </label>

      {selectedName && rolesForName.length > 0 && (
        <div className="persona-picker__roles">
          <span className="field__label">{t('persona.role')}</span>
          <div className="persona-picker__grid">
            {rolesForName.map((p) => {
              const taken = takenIds.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="persona-card"
                  disabled={busy}
                  onClick={() => void claim(p)}
                >
                  <span className="persona-card__name">{p.role}</span>
                  {taken && <span className="persona-card__badge">{t('persona.taken')}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
